import {useQuery, useMutation, useQueryClient, UseQueryOptions} from "@tanstack/react-query";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {getImproveAPI} from "../gen";
import {
	GetCohortSettingsResponse,
	UpdateCohortSettingsBody,
	UpdateCohortSettingsResponse
} from "../gen";
import {QUERY_KEYS} from "./user";

/**
 * Query Keys for Cohort Settings
 */
export const COHORT_SETTINGS_QUERY_KEYS = {
	all: ["cohort-settings"] as const,
	detail: (cohortId: number) => [...COHORT_SETTINGS_QUERY_KEYS.all, cohortId] as const
};

/**
 * Get cohort settings
 * 
 * Fetches settings for a specific cohort including daily limit, auto-assignment,
 * comments, extended access, and other admin preferences.
 * 
 * @example
 * ```typescript
 * const { data: settings, isLoading } = useGetCohortSettings(cohortId);
 * 
 * if (settings) {
 *   console.log('Daily limit:', settings.dailyLimit);
 *   console.log('Auto-assignment:', settings.trainingFeedAutoAssignment);
 * }
 * ```
 */
export const useGetCohortSettings = (
	cohortId: number,
	options?: Omit<UseQueryOptions<GetCohortSettingsResponse, Error>, "queryKey" | "queryFn">
) => {
	const axiosConfig = useImproveAxios();
	const {cohortsControllerGetSettings} = getImproveAPI();

	return useQuery<GetCohortSettingsResponse, Error>({
		queryKey: COHORT_SETTINGS_QUERY_KEYS.detail(cohortId),
		queryFn: async () => {
			const response = await cohortsControllerGetSettings(cohortId, axiosConfig);
			return response.data;
		},
		enabled: !!cohortId,
		...options
	});
};

/**
 * Update cohort settings
 * 
 * Updates cohort settings including daily limit, auto-assignment preferences,
 * comments, privacy, and extended access cohorts.
 * 
 * On success, invalidates the cohort settings cache to refetch updated data.
 * 
 * @example
 * ```typescript
 * const { mutate: updateSettings, isPending } = useUpdateCohortSettings(cohortId);
 * 
 * updateSettings({
 *   dailyLimit: 2,
 *   trainingFeedAutoAssignment: false,
 *   clearExistingEnrollments: true,
 * });
 * ```
 */
export const useUpdateCohortSettings = (cohortId: number) => {
	const axiosConfig = useImproveAxios();
	const {cohortsControllerUpdateSettings} = getImproveAPI();
	const queryClient = useQueryClient();

	return useMutation<UpdateCohortSettingsResponse, Error, UpdateCohortSettingsBody>({
		mutationFn: async (settings: UpdateCohortSettingsBody) => {
			const response = await cohortsControllerUpdateSettings(cohortId, settings, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate cohort settings cache to refetch with updated data
			queryClient.invalidateQueries({
				queryKey: COHORT_SETTINGS_QUERY_KEYS.detail(cohortId)
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.ME]
			});
		}
	});
};

